// Adds the ability to detect leaked Vue instances and provides utility functions to help debug them.
//
// Usage:
// 1. Enable leak detection in console (automatically enabled in development mode)
//      $app.$enableVueLeakDetection()
// 2. Use the following functions to debug:
//      $app.$destroyedVueInfo() - Print info about destroyed Vue instances
//      $app.$destroyedVuesByName('ComponentName') - Get destroyed Vue instances by name
//      $app.$containmentPaths(targetObject) - Get paths to the target object from Vue instance

import { containmentPaths } from 'src/utils/ContainmentPathFinder'
import WeakRefSet from 'src/utils/WeakRefSet'

let destroyedVues = new WeakRefSet()

function destroyedVueInfo (includeQuasarComponents = false) {
  let vues = destroyedVues.items()

  if (!includeQuasarComponents) {
    vues = _.filter(vues, vue => !vue.$options?.name?.startsWith('Q'))
  }

  const groupedVueNames = _(vues)
    .map('$options.name')
    .groupBy()
    .map((v, k) => [k, v.length])
    .sortBy(p => -p[1])
    .map(p => `${p[0]}->${p[1]}`)
    .value()
    .join('\n')

  console.log(`Destroyed Vue instances still in memory (${vues.length}):\n${groupedVueNames}`)
}

function destroyedVuesByName (name) {
  const vues = destroyedVues.items()
  return _.filter(vues, vue => vue.$options.name === name)
}

function containmentPathsHelper (targetObject, root, keysOnly = false, fullPath = false) {
  return containmentPaths(targetObject, root, keysOnly, fullPath)
}

let leakTimer
function monitorLeaks (interval = 2000) {
  if (leakTimer) {
    clearInterval(leakTimer)
  }

  leakTimer = setInterval(() => {
    destroyedVueInfo()
  }, interval)
}

function stopLeakMonitoring () {
  if (leakTimer) {
    clearInterval(leakTimer)
  }
}

let leakDetectionEnabled = false
function enableVueLeakDetection (app) {
  if (leakDetectionEnabled) {
    return
  }

  destroyedVues = new WeakRefSet()

  app.mixin({
    unmounted () {
      this._isDestroyed = true
      destroyedVues.add(this)
    }
  })

  app.$destroyedVueInfo = destroyedVueInfo
  app.$destroyedVuesByName = destroyedVuesByName
  app.$containmentPaths = containmentPathsHelper
  app.$monitorLeaks = monitorLeaks
  app.$stopLeakMonitoring = stopLeakMonitoring

  leakDetectionEnabled = true
}

export default async ({ app }) => {
  app.$enableVueLeakDetection = () => enableVueLeakDetection(app)

  // Uncomment to enable leak detection in development mode
  // enableVueLeakDetection(app)
}
